import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Search, Download, Mail } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

const STATUS_COLORS = { draft: 'bg-muted text-foreground', approved: 'bg-success/20 text-success', cancelled: 'bg-error/20 text-error' };

export default function DebitNotesPage() {
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [form, setForm] = useState({ customer_id: '', customer_name: '', invoice_id: '', date: localToday(), reason: '', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [r, c, inv] = await Promise.all([api.get('/debit-notes'), api.get('/customers'), api.get('/invoices?limit=5000')]);
      setDocs(r.data || []); setCustomers(c.data?.data || c.data || []); setInvoices(inv.data?.data || inv.data || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const calcTotal = (items) => items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);

  const save = async () => {
    const items = form.items.filter(i => i.description);
    const subtotal = calcTotal(items);
    try {
      if (editDoc) await api.put(`/debit-notes/${editDoc.id}`, { ...form, items, subtotal, total: subtotal });
      else await api.post('/debit-notes', { ...form, items, subtotal, total: subtotal });
      toast.success(editDoc ? 'Updated' : 'Created'); setShowForm(false); setEditDoc(null); fetchData();
    } catch { toast.error('Save failed'); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/debit-notes/${id}`); toast.success('Deleted'); fetchData(); };
  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/debit-notes/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `debit_note_${id.slice(0, 8)}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
  };
  const sendEmail = async () => {
    if (!emailTo || !emailDialog) return;
    try {
      const r = await api.post(`/debit-notes/${emailDialog}/email`, { email: emailTo });
      if (r.data?.success) { toast.success(r.data.message); setEmailDialog(null); setEmailTo(''); }
      else toast.error(r.data?.error || 'Send failed');
    } catch { toast.error('Email send failed'); }
  };
  const openNew = () => { setEditDoc(null); setForm({ customer_id: '', customer_name: '', invoice_id: '', date: localToday(), reason: '', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] }); setShowForm(true); };
  const openEdit = (d) => { setEditDoc(d); setForm({ customer_id: d.customer_id, customer_name: d.customer_name, invoice_id: d.invoice_id || '', date: d.date, reason: d.reason || '', notes: d.notes || '', items: d.items?.length ? d.items : [{ description: '', quantity: 1, unit_price: 0 }] }); setShowForm(true); };

  const filtered = docs.filter(d => !search || d.reference_id?.toLowerCase().includes(search.toLowerCase()) || d.customer_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="debit-notes-page">
        <div className="flex items-center justify-between">
          <div><h1 className="text-xl font-bold text-foreground">Debit Notes</h1><p className="text-sm text-muted-foreground">Additional charges issued to customers</p></div>
          <Button onClick={openNew} data-testid="create-debit-note-btn"><Plus size={16} className="mr-1" /> Create Debit Note</Button>
        </div>
        <div className="relative max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b"><tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reason</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No debit notes yet</td></tr> :
                 filtered.map(d => (
                  <tr key={d.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{d.reference_id}</td>
                    <td className="px-4 py-3">{d.customer_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.date}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{d.reason}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_COLORS[d.status] || 'bg-muted'}>{d.status}</Badge></td>
                    <td className="px-4 py-3 text-right font-medium">{(d.total || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} className="text-xs h-7">Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(d.id)} className="text-xs h-7 text-orange-600" data-testid={`debit-pdf-${d.id}`}><Download size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEmailDialog(d.id); setEmailTo(''); }} className="text-xs h-7 text-info" data-testid={`debit-email-${d.id}`}><Mail size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(d.id)} className="text-xs h-7 text-error"><Trash2 size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editDoc ? 'Edit' : 'New'} Debit Note</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Customer</label>
                <Select value={form.customer_id} onValueChange={v => { const c = customers.find(x => x.id === v); setForm({...form, customer_id: v, customer_name: c?.name || ''}); }}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.ref ? ` (${c.ref})` : ''}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Related Invoice</label>
              <Select value={form.invoice_id} onValueChange={v => setForm({...form, invoice_id: v})}>
                <SelectTrigger><SelectValue placeholder="Optional - link to invoice" /></SelectTrigger>
                <SelectContent>{invoices.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number || inv.reference_id} - {inv.customer_name || inv.customer}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Reason</label><Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Reason for debit note" /></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-2 block">Line Items</label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Description" value={item.description} onChange={e => { const items = [...form.items]; items[i].description = e.target.value; setForm({...form, items}); }} className="flex-1" />
                  <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => { const items = [...form.items]; items[i].quantity = e.target.value; setForm({...form, items}); }} className="w-20" />
                  <Input type="number" placeholder="Price" value={item.unit_price} onChange={e => { const items = [...form.items]; items[i].unit_price = e.target.value; setForm({...form, items}); }} className="w-28" />
                  <Button variant="ghost" size="sm" onClick={() => { const items = form.items.filter((_, j) => j !== i); setForm({...form, items: items.length ? items : [{ description: '', quantity: 1, unit_price: 0 }]}); }}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setForm({...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }]})} className="text-xs">+ Add Line</Button></div>
            <div className="text-right text-lg font-bold">Total: {calcTotal(form.items).toLocaleString('en', { minimumFractionDigits: 2 })}</div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button><Button onClick={save} data-testid="save-debit-note-btn">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!emailDialog} onOpenChange={() => setEmailDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Send via Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@email.com" type="email" data-testid="email-input" />
            <p className="text-xs text-muted-foreground">PDF will be attached to the email</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailDialog(null)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={!emailTo} data-testid="send-email-btn"><Mail size={14} className="mr-1" /> Send</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}